import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import { FilterProps } from './filterAndSearchUtils';

/**
 * Common utility functions for request filtering at the component level.
 * These functions complement the context-level filtering in filterAndSearchUtils.ts
 * by providing additional filtering capabilities for components that need more
 * fine-grained control over what requests are displayed.
 */

/**
 * Filter requests by status and split into Blocker and Normal priority groups.
 * Used by both desktop and mobile components to ensure consistent filtering behavior.
 *
 * @param requests - Array of requests to filter and split
 * @param status - Status to filter by ('Open', 'In Progress', 'Closed')
 * @returns Object containing filtered Blocker and Normal request arrays
 */
export function filterAndSplitByPriority(requests: AskRequest[], status: FilterProps['status']) {
  // First filter by current status - only apply status filter to the requests
  // This allows search results to show up regardless of status
  const statusFilteredRequests = requests.filter(req => req.status === status);

  // Then split into blocker and normal
  const blockersArray: AskRequest[] = [];
  const normalArray: AskRequest[] = [];

  statusFilteredRequests.forEach(req => {
    // @ts-ignore - 'priority' field exists at runtime but may not be in type definition
    if (req?.priority === 'Blocker') {
      blockersArray.push(req);
    } else {
      normalArray.push(req);
    }
  });

  return {
    blockerRequests: blockersArray,
    normalRequests: normalArray,
  };
}

/**
 * Utility function to get a safe reference to filtered requests with proper fallbacks.
 * Checks if priorityGroups structure exists and provides empty arrays as fallbacks.
 *
 * @param priorityGroups - The priorityGroups object from QueueContext
 * @returns Object containing safe references to Blocker and Normal request arrays
 */
export function getSafeRequestReferences(priorityGroups: any) {
  return {
    blockerRequests: Array.isArray(priorityGroups?.Blocker) ? priorityGroups.Blocker : [],
    normalRequests: Array.isArray(priorityGroups?.Normal) ? priorityGroups.Normal : [],
  };
}
